import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import { PiKanban, PiTrash } from "react-icons/pi";
import api from "../chat/api";
import {
  PageHeader,
  SecondaryButton,
  GhostButton,
  Toggle,
  ListCard,
  ListRow,
  StatusPill,
  ViewOnlyBanner,
  TextInput,
  useSettings,
} from "./settings/ui";

const DISABLE_HINT =
  "This status is in use. Remove it from all applied projects first, then you can disable it.";

// Null when the toggle is free; enabling a disabled status is always allowed.
const lockReasonFor = (s) => {
  if (s.label === "Draft") return "Draft can't be disabled";
  if (s.is_enabled && s.in_use) return DISABLE_HINT;
  return null;
};

const sortNewestFirst = (arr) =>
  [...arr].sort((a, b) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return (b.id || 0) - (a.id || 0);
  });

const displayLabel = (s) => (s.label === "Win" ? "Won" : s.label);

const Dot = ({ color }) => (
  <span
    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
    style={{ backgroundColor: color || "#8B5CF6" }}
  />
);

const StatusToggle = ({ s, savingId, onToggle }) => {
  const reason = lockReasonFor(s);
  return (
    <Toggle
      checked={s.is_enabled}
      disabled={!!reason || savingId === s.id}
      title={reason || undefined}
      aria-label={`${s.is_enabled ? "Turn off" : "Turn on"} ${displayLabel(s)}`}
      onChange={onToggle}
    />
  );
};

const Section = ({ label, note, children, className = "", cardClassName = "" }) => (
  <div className={className}>
    <div className="mb-2 shrink-0">
      <div className="text-[14px] font-semibold text-[#18101C]">{label}</div>
      <div className="mt-0.5 text-[13px] text-[#71717A]">{note}</div>
    </div>
    <ListCard className={cardClassName}>{children}</ListCard>
  </div>
);

const RenameModal = ({ initialName = "", onClose, onSubmit, saving }) => {
  const [name, setName] = useState(initialName);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = name.trim();
  const canSubmit = !saving && trimmed.length > 0 && trimmed !== initialName.trim();

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[16px] shadow-xl w-full max-w-[460px] p-6 font-outfit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 className="text-[20px] font-semibold text-[#18101C]">Rename status</h2>
          <button
            onClick={onClose}
            className="text-[#71717A] hover:text-[#18181B] transition-colors"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <p className="text-[14px] text-[#71717A] mt-1">
          The new name applies to everyone in the workspace.
        </p>

        <label className="block text-[14px] font-medium text-[#27272A] mt-5 mb-1.5">
          Status name
        </label>
        <TextInput
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) onSubmit(trimmed);
          }}
          placeholder="Enter status name"
          className="max-w-none"
        />

        <div className="flex justify-end gap-3 mt-6">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSubmit(trimmed)}
            className="h-9 px-4 rounded-[8px] text-[14px] font-medium text-[#FAFAFA] bg-[#A04A97] hover:bg-[#8E4186] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ onClose, onConfirm, deleting }) => (
  <div
    className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-[16px] shadow-xl w-full max-w-[460px] p-6 font-outfit"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="text-[20px] font-semibold text-[#18101C]">Delete this custom status?</h2>
      <p className="text-[14px] text-[#71717A] mt-1.5">
        This action is permanent and cannot be undone.
      </p>
      <div className="flex justify-end gap-3 mt-6">
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <button
          type="button"
          disabled={deleting}
          onClick={onConfirm}
          className="h-9 px-4 rounded-[8px] text-[14px] font-medium text-white bg-[#E21919] hover:bg-[#C81515] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {deleting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

const StatusConfiguration = () => {
  const { canManage, roleKnown } = useSettings();
  const [defaults, setDefaults] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  // { type: "edit" | "delete", status }
  const [modal, setModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const orgId = Cookies.get("selected_org_id");

  const loadStatuses = async () => {
    if (!orgId) {
      setLoading(false);
      toast.error("No workspace selected");
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get(`/api/v1/org/project-statuses/?organization_id=${orgId}`);
      setDefaults(Array.isArray(res.data?.default) ? res.data.default : []);
      setCustom(sortNewestFirst(Array.isArray(res.data?.custom) ? res.data.custom : []));
    } catch (error) {
      console.error("Error fetching project statuses:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const defaultEnabled = defaults.filter((s) => s.is_enabled).length;

  // Optimistic, rolls back on failure. setList is the matching state setter.
  const toggleStatus = async (statusItem, setList) => {
    const next = !statusItem.is_enabled;
    setSavingId(statusItem.id);
    setList((prev) =>
      prev.map((s) => (s.id === statusItem.id ? { ...s, is_enabled: next } : s))
    );
    try {
      await api.patch(
        `/api/v1/org/project-statuses/${statusItem.id}/?organization_id=${orgId}`,
        { is_enabled: next }
      );
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error(error?.response?.data?.error || "Failed to update status");
      setList((prev) =>
        prev.map((s) => (s.id === statusItem.id ? { ...s, is_enabled: !next } : s))
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || adding) return;
    setAdding(true);
    try {
      const res = await api.post(
        `/api/v1/org/project-statuses/?organization_id=${orgId}`,
        { label: name }
      );
      setCustom((prev) => sortNewestFirst([res.data, ...prev]));
      toast.success("Status added");
      setNewName("");
    } catch (error) {
      console.error("Error adding status:", error);
      toast.error(error?.response?.data?.error || "Failed to add status");
    } finally {
      setAdding(false);
    }
  };

  const handleEdit = async (name) => {
    const id = modal?.status?.id;
    if (!id) return;
    setSubmitting(true);
    try {
      const res = await api.patch(
        `/api/v1/org/project-statuses/${id}/?organization_id=${orgId}`,
        { label: name }
      );
      setCustom((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      toast.success("Status updated");
      setModal(null);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error?.response?.data?.error || "Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const id = modal?.status?.id;
    if (!id) return;
    setSubmitting(true);
    const prev = custom;
    try {
      await api.delete(`/api/v1/org/project-statuses/${id}/?organization_id=${orgId}`);
      setCustom((p) => p.filter((s) => s.id !== id));
      toast.success("Status deleted");
      setModal(null);
    } catch (error) {
      console.error("Error deleting status:", error);
      toast.error(error?.response?.data?.error || "Failed to delete status");
      setCustom(prev);
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <PageHeader
      title="Project statuses"
      description="The stages a project moves through, available in every project in this workspace."
    />
  );

  if (loading) {
    return (
      <div className="w-full font-outfit">
        {header}
        <div className="max-w-[760px] py-16 flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-[#E4E4E7] border-t-[#A04A97] rounded-full animate-spin"></div>
          <p className="text-[14px] text-[#71717A]">Loading project statuses...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="w-full font-outfit">
        {header}
        <div className="max-w-[760px] py-16 flex flex-col items-center gap-3 text-center">
          <p className="text-[14px] text-[#71717A]">We couldn't load project statuses.</p>
          <SecondaryButton onClick={loadStatuses}>Try again</SecondaryButton>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col font-outfit">
      {header}

      {roleKnown && !canManage && (
        <ViewOnlyBanner icon={PiKanban}>
          Statuses are shared by every project. Admins add, rename or turn them off.
        </ViewOnlyBanner>
      )}

      <div className="max-w-[760px] flex-1 min-h-0 flex flex-col gap-5">
        <Section
          className="shrink-0"
          label="Default statuses"
          note={`${defaultEnabled} of ${defaults.length} on · can be turned off, not renamed`}
        >
          {defaults.map((s) => (
            <ListRow key={s.id} off={!s.is_enabled} compact>
              <Dot color={s.color} />
              <span className="flex-1 min-w-0 truncate text-[14px] text-[#18101C]">
                {displayLabel(s)}
              </span>
              {canManage ? (
                <StatusToggle
                  s={s}
                  savingId={savingId}
                  onToggle={() => toggleStatus(s, setDefaults)}
                />
              ) : (
                !s.is_enabled && <StatusPill tone="neutral">Off</StatusPill>
              )}
            </ListRow>
          ))}
        </Section>

        <Section
          label="Custom statuses"
          note="Newest first"
          className="flex-1 min-h-[160px] flex flex-col"
          cardClassName="flex-1 min-h-0 flex flex-col"
        >
          {canManage && (
            <div className="flex items-center gap-3 px-[18px] py-2 bg-[#FCFAFD] border-b border-black/[0.07] shrink-0">
              <TextInput
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
                placeholder="New status name, e.g. Awaiting clarification"
                className="flex-1 max-w-none !h-9 !text-[14px]"
              />
              <SecondaryButton
                onClick={handleCreate}
                disabled={!newName.trim() || adding}
                className="shrink-0 !h-9"
              >
                {adding ? "Adding..." : "Add status"}
              </SecondaryButton>
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {custom.map((s) => (
            <ListRow key={s.id} off={!s.is_enabled} compact>
              <Dot color={s.color} />
              <span className="flex-1 min-w-0 truncate text-[14px] text-[#18101C]">
                {s.label}
              </span>
              {canManage ? (
                <div className="flex items-center gap-1 shrink-0">
                  <div className="mr-2 flex items-center">
                    <StatusToggle
                      s={s}
                      savingId={savingId}
                      onToggle={() => toggleStatus(s, setCustom)}
                    />
                  </div>
                  <GhostButton className="!h-8 !px-2.5" onClick={() => setModal({ type: "edit", status: s })}>
                    Rename
                  </GhostButton>
                  <button
                    type="button"
                    aria-label={`Delete ${s.label}`}
                    onClick={() => setModal({ type: "delete", status: s })}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-[8px] text-[#71717A] hover:text-[#E21919] hover:bg-[rgba(226,25,25,0.06)] transition-colors"
                  >
                    <PiTrash className="text-[16px]" />
                  </button>
                </div>
              ) : (
                !s.is_enabled && <StatusPill tone="neutral">Off</StatusPill>
              )}
            </ListRow>
          ))}
          {custom.length === 0 && (
            <ListRow compact>
              <span className="text-[14px] text-[#71717A]">No custom statuses yet.</span>
            </ListRow>
          )}
          </div>
        </Section>
      </div>

      {modal?.type === "edit" && (
        <RenameModal
          initialName={modal.status?.label || ""}
          saving={submitting}
          onClose={() => setModal(null)}
          onSubmit={handleEdit}
        />
      )}
      {modal?.type === "delete" && (
        <DeleteConfirmModal
          deleting={submitting}
          onClose={() => setModal(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default StatusConfiguration;
